import * as React from 'react';
import {
  Img,
  Heading,
  Row,
  Section,
  Column,
  Container,
  Link,
  Head,
  Body,
} from '@react-email/components';
import { Text } from '@react-email/text';
import { Html } from '@react-email/components';
import { Footer_v2, Header, SimilarEvents, Divider } from '../components';
import { KYDEventSummary } from '../../interfaces/KYDEvent';
import { KYDSocials } from '../../interfaces/KYDOrganization';

export const name = 'EventCanceled';
export const subject = '{{event_name}} at {{venue_name}} Has Been Canceled';

const RoundedContainer = ({
  children,
  style = {},
}: {
  children: React.ReactNode;
  style?: React.CSSProperties;
}) => {
  return (
    <div
      className="email-container"
      style={{
        width: '100%',
        maxWidth: 600,
        margin: '0 auto',
        marginBottom: '16px',
        border: '1px solid #B9B9B9',
        borderRadius: '4px',
        ...style,
      }}
    >
      {children}
    </div>
  );
};

export function template({
  event_name,
  event_url,
  event_image,
  display_previous_start_at,
  display_updated_start_at,
  display_updated_start_at_date,
  first_name,
  last_name,
  venue_name,
  venue_address,
  message_from_venue,
  additional_message,
  preheader,
  ticket_type_names,
  quantity,
  last_date_for_refund,
  force_desktop = false,
  related_shows,
  organization_website,
  socials,
  campaign_id,
}: {
  event_name: string;
  event_url: string;
  event_image: string;
  display_previous_start_at: string;
  display_updated_start_at: string;
  display_updated_start_at_date: string;
  first_name: string;
  last_name: string;
  venue_name: string;
  venue_address: string;
  message_from_venue: string;
  additional_message: string;
  preheader: string;
  ticket_type_names: string | string[];
  quantity: number | string;
  last_date_for_refund: string;
  force_desktop?: boolean;
  related_shows?: KYDEventSummary[];
  organization_website: string;
  socials: KYDSocials;
  campaign_id?: string;
}) {
  const logo_url = 'https://content.kydlabs.com/system/kyd-email-logo.png';
  return (
    <Html>
      <Head>
        <title>{`${event_name} - Rescheduled`}</title>
        <style>
          {`

          .mobile-only {
            display: none !important;
          }

          .desktop-only {
            display: block !important;
          }

        `}
        </style>
        <style>
          {`
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;700&display=swap');

          * {
            font-family: 'Inter', sans-serif;
          }

        `}
        </style>
        {!force_desktop && (
          <style>
            {`
            @media only screen and (max-width:596px) {  
              .mobile-only {
                display: block !important;
              }
    
              .desktop-only {
                display: none !important;
              }
              .button {
                width: 180px !important;
              }
              .chevron-right {
                margin-left: 16px !important;
              }
            }
            `}
          </style>
        )}
      </Head>

      <Body
        style={{
          width: '100%',
          maxWidth: '600px',
          margin: '24px auto',
        }}
      >
        {preheader && (
          <Text
            style={{
              display: 'none',
              fontSize: '1px',
              lineHeight: '1px',
              maxHeight: '0px',
              maxWidth: '0px',
              opacity: 0,
              overflow: 'hidden',
              visibility: 'hidden',
            }}
          >
            {preheader}
            {Array(100).fill('\u200C\u00A0').join('')}
          </Text>
        )}
        <Header src={logo_url} style={{ margin: '0px auto' }} />
        <Container>
          <Heading as="h1" style={eventName}>
            {event_name}
            <br />
            show rescheduled
          </Heading>
          <Heading as="h3" style={{ ...label, fontSize: '16px' }}>
            Your current ticket is valid for the new date.
            <br />
            Details below.
          </Heading>
        </Container>
        <RoundedContainer>
          <Section
            style={{
              width: '100%',
              backgroundColor: '#fff',
              borderRadius: '4px',
            }}
          >
            <Row>
              <Column
                style={{
                  paddingTop: '16px',
                  paddingBottom: '16px',
                  paddingLeft: '12px',
                  paddingRight: '12px',
                }}
              >
                <Row className="mobile-only">
                  <Column
                    style={{
                      display: 'block',
                      width: '100%',
                    }}
                  >
                    <div
                      style={{
                        display: 'block',
                        width: '100%',
                      }}
                    >
                      <div
                        style={{
                          display: 'block',
                        }}
                      >
                        <Img
                          src={event_image}
                          width="100%"
                          style={{
                            borderRadius: '4px',
                            marginBottom: '16px',
                            display: 'block',
                          }}
                        />
                      </div>

                      <div className="mobile-only">
                        {message_from_venue?.split('\n').map((line, i) => (
                          <Text key={i} style={bodyText}>
                            {line || <br />}
                          </Text>
                        ))}
                      </div>
                    </div>
                  </Column>
                </Row>

                {/* Desktop */}
                <Row className="desktop-only">
                  <Column style={{ width: '55%', verticalAlign: 'top' }}>
                    {message_from_venue?.split('\n').map((line, i) => (
                      <Text key={i} style={bodyText}>
                        {line || <br />}
                      </Text>
                    ))}
                  </Column>
                  <Column style={{ width: '10px', verticalAlign: 'top' }} />
                  <Column
                    style={{
                      width: '40%',
                      verticalAlign: 'top',
                      textAlign: 'center',
                    }}
                  >
                    <Img
                      src={event_image}
                      width="100%"
                      style={{
                        borderRadius: '4px',
                        display: 'block',
                        marginBottom: '16px',
                      }}
                    />
                  </Column>
                </Row>

                {additional_message && (
                  <Row>
                    <Column>
                      <Text style={titleText}>Message from the venue</Text>
                      <Text
                        style={{
                          ...titleText,
                          fontWeight: 'normal',
                          color: '#000000CC',
                        }}
                      >
                        {additional_message}
                      </Text>
                    </Column>
                  </Row>
                )}
                <Divider />
                <Row>
                  <Column style={{ verticalAlign: 'top' }}>
                    <Text style={titleText}>Date & Time</Text>
                  </Column>

                  <Column
                    style={{
                      textAlign: 'right',
                    }}
                  >
                    <Text
                      style={{
                        ...infoText,
                        fontWeight: 'normal',
                        textDecoration: 'line-through',
                        marginBottom: '0px',
                      }}
                    >
                      {display_previous_start_at}
                    </Text>
                    <Text style={{ ...infoText, marginTop: '0px' }}>
                      {display_updated_start_at}
                    </Text>
                  </Column>
                </Row>
                <Divider />
                <Row>
                  <Column style={{ verticalAlign: 'top' }}>
                    <Text style={titleText}>Venue</Text>
                  </Column>
                  <Column
                    style={{
                      textAlign: 'right',
                    }}
                  >
                    <Text style={{ ...infoText, marginBottom: '18px' }}>
                      {venue_name}
                    </Text>
                    <Text
                      style={{
                        ...infoText,
                        marginBottom: '10px',
                        marginTop: '0px',
                      }}
                    >
                      {venue_address}
                    </Text>
                  </Column>
                </Row>
                <Divider />
                <Row>
                  <Column style={{ verticalAlign: 'top' }}>
                    <Text style={titleText}>Ticket Type</Text>
                  </Column>
                  <Column style={{ textAlign: 'right' }}>
                    <Text style={infoText}>
                      <ul style={listStyle}>
                        {typeof ticket_type_names !== 'string'
                          ? ticket_type_names.map((name, index) => (
                              <li key={index}>{name}</li>
                            ))
                          : ticket_type_names}
                      </ul>
                    </Text>
                  </Column>
                </Row>
                <Divider />
                <Row>
                  <Column style={{ verticalAlign: 'top' }}>
                    <Text style={titleText}>Quantity</Text>
                  </Column>
                  <Column style={{ textAlign: 'right' }}>
                    <Text style={infoText}>{quantity}</Text>
                  </Column>
                </Row>
                <Divider />
                <Row>
                  <Column style={{ verticalAlign: 'top' }}>
                    <Text style={titleText}>Name on Ticket</Text>
                  </Column>
                  <Column
                    style={{
                      textAlign: 'right',
                    }}
                  >
                    <Text style={infoText}>
                      {first_name} {last_name}
                    </Text>
                  </Column>
                </Row>
                <Divider
                  style={{
                    display: 'none',
                  }}
                  className="mobile-only"
                />
                <Row style={{ marginTop: '20px' }}>
                  <Column
                    style={{
                      textAlign: 'center',
                    }}
                  >
                    <Link
                      className="button"
                      href={`${event_url}/wallet?ref=event-rescheduled-email`}
                      style={{
                        backgroundColor: '#000000',
                      }}
                    >
                      <Img
                        src={
                          'https://content.kydlabs.com/system/view-tickets-button.png'
                        }
                        style={{
                          width: '180px',
                          verticalAlign: 'middle',
                          margin: '0 auto',
                        }}
                      />
                    </Link>
                  </Column>
                </Row>
              </Column>
            </Row>
            <Row>
              <Divider />
              <Text
                style={{
                  textAlign: 'center',
                  fontSize: '12px',
                  lineHeight: '22px',
                  marginBottom: '10px',
                  marginTop: '10px',
                  color: '#000000CC',
                  fontWeight: '500',
                  fontFamily: 'Inter, sans-serif',
                  paddingLeft: '12px',
                  paddingRight: '12px',
                }}
              >
                If you can't make it on {display_updated_start_at_date}, please
                reply to this <br /> email by {last_date_for_refund} for a full
                refund.
              </Text>
            </Row>
          </Section>
        </RoundedContainer>
        {related_shows.length > 0 && <Divider />}
        {related_shows.length > 0 && (
          <SimilarEvents
            events={related_shows}
            campaign_id={campaign_id || 'rescheduled'}
            source="event-rescheduled-email"
          />
        )}
        <Footer_v2
          src={logo_url}
          organization_website={organization_website}
          socials={socials}
        />
      </Body>
    </Html>
  );
}

const listStyle = {
  listStyleType: 'none',
  padding: 0,
  margin: 0,
  fontSize: '14px',
  color: 'black',
  fontWeight: '600',
  lineHeight: '22px',
  fontFamily: 'Inter, sans-serif',
};

const bodyText = {
  fontSize: '12px',
  lineHeight: '18px',
  marginTop: '0px',
  color: '#000000CC',
  fontWeight: '500',
  fontFamily: 'Inter, sans-serif',
};

const titleText = {
  fontSize: '14px',
  lineHeight: '22px',
  marginBottom: '10px',
  marginTop: '10px',
  color: 'black',
  fontWeight: '500',
  fontFamily: 'Inter, sans-serif',
};

const infoText = {
  ...titleText,
  fontWeight: 'bold',
};

const base = {
  color: '#000000CC',
  fontFamily: 'Inter, sans-serif',
};

const eventName: React.CSSProperties = {
  ...base,
  textTransform: 'uppercase',
  textAlign: 'center',
  fontSize: '26px',
  fontWeight: '600',
  marginTop: '20px',
  lineHeight: '32px',
};

const label = {
  ...base,
  fontSize: '18px',
  marginBottom: '24px',
  marginTop: '10px',
  fontWeight: 'normal',
  textAlign: 'center' as const,
};

template.PreviewProps = {
  name: 'EventRescheduled',
  event_name: 'Test event',
  event_url: '/static/coachella.jpg',
  start_at: new Date().toISOString(),
  ticket_type_name: 'Table seat',
  quantity: '4',
  venue_name: 'LPR',
  venue_address: '55 N 5th St',
  event_description: `Test **description** 23456 Here's a link to support: [https://help.kydlabs.com](https://help.kydlabs.com)`,
  event_image: '',
  logo_url: '',
  display_start_at: '',
  full_name: '',
  display_updated_start_at: '',
  message_from_venue: '',
  additional_message: '',
  subject: '',
};

export default template;
